<template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">history_edu</span>
      <p>
        Registra y actualiza los antecedentes clínicos del paciente clasificados
        por tipo: personales, familiares, otros patológicos y obstétricos.
      </p>
    </div>

    <!-- Tipos de antecedentes -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">category</span>
        Tipos de antecedentes
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in tipos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Campos del formulario -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">edit_note</span>
        Campos del formulario
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in campos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Parentesco -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">family_restroom</span>
        Parentesco (antecedentes familiares)
      </h3>
      <p class="help-card-desc" style="padding:0 0.25rem;margin-bottom:0.5rem">
        Visible únicamente al editar antecedentes de tipo <strong>Familiar</strong>.
        Puedes seleccionar el parentesco del desplegable o usar los botones
        rápidos para asignarlo con un solo clic:
      </p>
      <div class="help-estados">
        <span class="help-estado-badge" style="background:#e3f2fd;color:#0d47a1">Padre</span>
        <span class="help-estado-badge" style="background:#fce4ec;color:#880e4f">Madre</span>
        <span class="help-estado-badge" style="background:#e3f2fd;color:#0d47a1">Abuelo Paterno</span>
        <span class="help-estado-badge" style="background:#fce4ec;color:#880e4f">Abuela Paterna</span>
        <span class="help-estado-badge" style="background:#e3f2fd;color:#0d47a1">Abuelo Materno</span>
        <span class="help-estado-badge" style="background:#fce4ec;color:#880e4f">Abuela Materna</span>
      </div>
    </section>

    <!-- Dictado por voz -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">mic</span>
        Dictado por voz
      </h3>
      <div class="help-voz">
        <div class="help-voz-item">
          <span class="material-symbols-outlined" style="color:orangered;font-size:28px">mic_off</span>
          <p class="help-card-desc">Micrófono <strong>inactivo</strong>. Pulsa para iniciar el dictado en el campo Observación.</p>
        </div>
        <div class="help-voz-item">
          <span class="material-symbols-outlined" style="color:green;font-size:28px">mic</span>
          <p class="help-card-desc">Micrófono <strong>activo</strong>. El sistema transcribe tu voz en tiempo real. Pulsa para detener.</p>
        </div>
      </div>
    </section>

    <!-- Restricción por estado -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">lock</span>
        Restricciones por estado de la atención
      </h3>
      <div class="help-estados" style="margin-bottom:0.5rem">
        <span class="help-estado-badge" style="background:#fff3cd;color:#856404">PENDIENTE</span>
        <span class="help-estado-badge" style="background:#cce5ff;color:#004085">PROCESO</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem">
        La edición de antecedentes solo está habilitada cuando la atención está
        en estado <strong>PENDIENTE</strong> o <strong>PROCESO</strong>. En
        cualquier otro estado la columna de edición no se muestra.
      </p>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        Si el motivo de la cita es obstétrico, el tipo
        <strong>Antecedentes Obstétricos</strong> se carga automáticamente
        al abrir la pestaña. En los demás casos selecciona el tipo del
        desplegable para ver y editar sus registros.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaAnteAten",
  data() {
    return {
      tipos: [
        {
          icon: "person",
          titulo: "Antecedentes Personales (P)",
          desc:   "Enfermedades, cirugías, hospitalizaciones y condiciones crónicas que el propio paciente ha padecido.",
        },
        {
          icon: "family_restroom",
          titulo: "Antecedentes Familiares (F)",
          desc:   "Enfermedades hereditarias o condiciones presentes en familiares directos. Requiere indicar el parentesco del familiar afectado.",
        },
        {
          icon: "medical_information",
          titulo: "Otros Antecedentes Patológicos (Y)",
          desc:   "Antecedentes clínicos relevantes que no encajan en las categorías personal ni familiar.",
        },
        {
          icon: "pregnant_woman",
          titulo: "Antecedentes Obstétricos (O)",
          desc:   "Visible solo cuando el motivo de la cita es obstétrico. Registra información sobre embarazos, partos y eventos gineco-obstétricos previos.",
        },
      ],
      campos: [
        {
          icon: "label",
          titulo: "Tipo",
          desc:   "Indica la categoría del antecedente (Personal, Familiar, Otros u Obstétrico). Se asigna automáticamente según el tipo seleccionado en el desplegable principal. No es editable en el modal.",
        },
        {
          icon: "history_edu",
          titulo: "Antecedente",
          desc:   "Nombre del antecedente clínico del catálogo del sistema. No es editable en el modal.",
        },
        {
          icon: "family_restroom",
          titulo: "Parentesco",
          desc:   "Solo visible en antecedentes familiares. Indica el familiar afectado. Puede seleccionarse del desplegable o con los botones rápidos.",
        },
        {
          icon: "pin",
          titulo: "Respuesta",
          desc:   "Aparece solo en algunos antecedentes. Puede ser un valor numérico (cantidad) o una selección Sí / No, según el tipo de respuesta definido para ese antecedente.",
        },
        {
          icon: "text_snippet",
          titulo: "Observación",
          desc:   "Campo de texto libre para detallar el antecedente. Admite dictado por voz. Máximo 250 caracteres. Campo obligatorio.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>